// src/pages/league-dashboard/salary-contract/components/BulkPriceAdjustmentModal.js
import React from 'react';
import { dynastyTheme } from '../../../../services/colorService';

const BulkPriceAdjustmentModal = ({
  selectedPlayers,
  generatedPrices,
  setGeneratedPrices,
  settings,
  setSelectedPlayers,
  setBulkEditMode,
  setPriceAdjustmentModal
}) => {

  const handleBulkPriceAdjustment = (adjustmentType, adjustmentValue) => {
    if (selectedPlayers.size === 0) {
      alert('No players selected');
      return;
    }

    const prices = [...(generatedPrices?.prices || [])];
    const updatedPrices = prices.map(player => {
      // Use canonical ids.mlb for matching (not flat player_id)
      const playerId = player?.ids?.mlb;
      if (selectedPlayers.has(playerId)) {
        const pr = player.pricing || {};
        let newPrice = pr.baseline_price;

        if (adjustmentType === 'percentage') {
          newPrice = pr.baseline_price * (1 + adjustmentValue / 100);
        } else if (adjustmentType === 'fixed') {
          newPrice = pr.baseline_price + adjustmentValue;
        } else if (adjustmentType === 'set') {
          newPrice = adjustmentValue;
        }

        newPrice = Math.round(newPrice / settings.salary_increment) * settings.salary_increment;
        newPrice = Math.max(settings.min_salary, newPrice);

        return {
          ...player,
          pricing: {
            ...pr,
            baseline_price: newPrice,
            manual_price: newPrice,
            _originalPrice: pr._originalPrice || pr.baseline_price
          }
        };
      }
      return player;
    });

    setGeneratedPrices({
      ...generatedPrices,
      prices: updatedPrices
    });

    setSelectedPlayers(new Set());
    setBulkEditMode(false);
    setPriceAdjustmentModal(false);

    // Recalculate summary stats locally
    const totalCost = updatedPrices.reduce((sum, p) => sum + (p.pricing?.baseline_price || 0), 0);
    const avgPrice = totalCost / updatedPrices.length;
    const maxPrice = Math.max(...updatedPrices.map(p => p.pricing?.baseline_price || 0));
    const minPrice = Math.min(...updatedPrices.map(p => p.pricing?.baseline_price || 0));
    const numTeams = settings.num_teams || 12;
    const avgTeamSpend = totalCost / numTeams;
    const capUsagePercent = settings.draft_cap ? Math.round((avgTeamSpend / settings.draft_cap) * 100) : 0;

    const newSummary = {
      totalCost: Math.round(totalCost),
      avgSalary: Math.round(avgPrice),  // Keep key name for compatibility
      maxSalary: Math.round(maxPrice),  // Keep key name for compatibility
      minSalary: Math.round(minPrice),  // Keep key name for compatibility
      avgTeamSpend: Math.round(avgTeamSpend),
      capUsagePercent
    };

    setGeneratedPrices(prev => ({
      ...prev,
      summary: newSummary
    }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
      <div className={`${dynastyTheme.components.card.base} p-6 max-w-md w-full`}>
        <h3 className={dynastyTheme.components.heading.h3}>
          Bulk Price Adjustment
        </h3>
        <p className={`${dynastyTheme.classes.text.neutralLight} mb-4`}>
          Adjusting {selectedPlayers.size} selected players
        </p>
        
        <div className="space-y-4">
          <div>
            <label className={dynastyTheme.components.label}>Adjustment Type</label>
            <select 
              id="adjustmentType"
              className={dynastyTheme.components.input}
            >
              <option value="percentage">Percentage Change</option>
              <option value="fixed">Fixed Amount</option>
              <option value="set">Set to Value</option>
            </select>
          </div>
          
          <div>
            <label className={dynastyTheme.components.label}>Value</label>
            <input
              type="number"
              id="adjustmentValue"
              className={dynastyTheme.components.input}
              placeholder="Enter value"
            />
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={() => {
                const type = document.getElementById('adjustmentType').value;
                const value = parseFloat(document.getElementById('adjustmentValue').value);
                if (!isNaN(value)) {
                  handleBulkPriceAdjustment(type, value);
                }
              }}
              className={dynastyTheme.utils.getComponent('button', 'primary', 'md')}
            >
              Apply
            </button>
            <button
              onClick={() => setPriceAdjustmentModal(false)}
              className={dynastyTheme.utils.getComponent('button', 'secondary', 'md')}
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkPriceAdjustmentModal;